import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, Target, Crown, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CATEGORIES = [
  'business-loans', 'commercial-finance', 'insurance', 'real-estate',
  'legal-services', 'accounting', 'marketing-services', 'other',
];
const GEOGRAPHIES = ['Australia-wide', 'NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
const DELIVERY_METHODS = [
  { value: 'dashboard', label: 'Dashboard (default)' },
  { value: 'email', label: 'Email digest' },
  { value: 'webhook', label: 'Webhook / CRM (coming soon)', disabled: true },
];
const CONTACT_AUTOMATION = [
  { value: 'sms', label: 'SMS (P1)', disabled: true },
  { value: 'voice', label: 'Voice (P1)', disabled: true },
  { value: 'calendar', label: 'Calendar booking (P1)', disabled: true },
];
const VERIFICATION_OPTIONS = [
  'sms_verified', 'email_verified', 'consent_captured', 'business_verified',
];

export default function DemandProfileCreatePage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/subscriptions/status');
        setMembership(res.data);
      } catch (e) { console.error(e); }
      finally { setMembershipLoading(false); }
    })();
  }, []);

  const [form, setForm] = useState({
    vertical: '',
    category: '',
    geography: ['Australia-wide'],
    monthly_volume: 20,
    target_cpl: 180,
    max_cpl: 250,
    required_fields: ['contact_name', 'phone'],
    qualification_criteria: '',
    exclusivity: 'shared',
    max_lead_age_hours: 72,
    verification_required: ['sms_verified'],
    delivery_method: ['dashboard'],
    contact_automation: [],
    monthly_budget: 5000,
    notes: '',
  });

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleList = (k, v) => setForm(f => ({
    ...f,
    [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v],
  }));

  const submit = async () => {
    if (!form.vertical.trim() || !form.category) {
      toast.error('Vertical and category are required');
      return;
    }
    if (form.max_cpl < form.target_cpl) {
      toast.error('Max CPL must be >= target CPL');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/demand-profiles', form);
      toast.success(`Demand Profile ${res.data.profile_id} created`);
      navigate('/buyer');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create demand profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/buyer')} className="mb-4 -ml-2 text-gray-600" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-md bg-[#0055FF] flex items-center justify-center flex-shrink-0">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium">Demand Profile</div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Post your demand</h1>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-8 max-w-xl">
          Tell sellers exactly what you buy: category, volume, CPL band and required verification.
          Your identity stays anonymous — only your buyer alias is visible.
        </p>

        {/* Membership pre-flight gate — block form when membership is inactive */}
        {!membershipLoading && !membership?.has_membership && (
          <div
            className="mb-6 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-5 flex flex-wrap items-center gap-4"
            data-testid="demand-membership-gate"
          >
            <div className="h-11 w-11 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-heading text-base font-semibold text-gray-900 flex items-center gap-2">
                Membership required to post demand
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded-full font-mono">
                  <AlertTriangle className="w-3 h-3" /> Required
                </span>
              </div>
              <div className="text-sm text-gray-700 mt-0.5">
                Demand Profiles are part of the $99/month Buyer Membership. Activate to continue.
              </div>
            </div>
            <Button
              onClick={() => navigate('/membership')}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="demand-gate-activate-btn"
            >
              <Crown className="w-4 h-4 mr-2" /> Activate — $99/mo
            </Button>
          </div>
        )}

        <div className={`border border-gray-200 rounded-lg p-6 space-y-6 ${!membershipLoading && !membership?.has_membership ? 'opacity-60 pointer-events-none' : ''}`}>
          {/* Section: What & Where */}
          <Section title="What & where">
            <Field label="Vertical" hint="Your industry name, e.g. Commercial Finance">
              <Input value={form.vertical} onChange={e => upd('vertical', e.target.value)} placeholder="e.g. Commercial Finance" data-testid="input-vertical" />
            </Field>
            <Field label="Lead category">
              <Select value={form.category} onValueChange={v => upd('category', v)}>
                <SelectTrigger data-testid="select-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Geography" hint="Choose one or more">
              <div className="flex flex-wrap gap-2">
                {GEOGRAPHIES.map(g => (
                  <button key={g} type="button" onClick={() => toggleList('geography', g)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-mono ${form.geography.includes(g) ? 'bg-[#0055FF] text-white border-[#0055FF]' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
                    data-testid={`geo-${g}`}
                  >{g}</button>
                ))}
              </div>
            </Field>
          </Section>

          {/* Section: Volume & pricing */}
          <Section title="Volume & pricing">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Monthly volume"><Input type="number" min="1" value={form.monthly_volume} onChange={e => upd('monthly_volume', parseInt(e.target.value) || 0)} data-testid="input-volume" /></Field>
              <Field label="Monthly budget (AUD)"><Input type="number" min="0" value={form.monthly_budget} onChange={e => upd('monthly_budget', parseFloat(e.target.value) || 0)} data-testid="input-budget" /></Field>
              <Field label="Target CPL"><Input type="number" min="0" value={form.target_cpl} onChange={e => upd('target_cpl', parseFloat(e.target.value) || 0)} data-testid="input-target-cpl" /></Field>
              <Field label="Max CPL"><Input type="number" min="0" value={form.max_cpl} onChange={e => upd('max_cpl', parseFloat(e.target.value) || 0)} data-testid="input-max-cpl" /></Field>
            </div>
          </Section>

          {/* Section: Quality */}
          <Section title="Quality bar">
            <Field label="Qualification criteria" hint="What makes a lead qualified for you?">
              <Textarea rows={3} value={form.qualification_criteria} onChange={e => upd('qualification_criteria', e.target.value)} placeholder="e.g. $100k+ funding requirement, ABN-registered business" data-testid="input-qualification" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Exclusivity">
                <Select value={form.exclusivity} onValueChange={v => upd('exclusivity', v)}>
                  <SelectTrigger data-testid="select-exclusivity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shared">Shared (up to 3 buyers)</SelectItem>
                    <SelectItem value="exclusive">Exclusive (only me)</SelectItem>
                    <SelectItem value="either">Either</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Max lead age (hours)">
                <Input type="number" min="1" value={form.max_lead_age_hours} onChange={e => upd('max_lead_age_hours', parseInt(e.target.value) || 0)} data-testid="input-max-age" />
              </Field>
            </div>
            <Field label="Verification required" hint="Only send leads with these verifications">
              <div className="flex flex-wrap gap-2">
                {VERIFICATION_OPTIONS.map(v => (
                  <button key={v} type="button" onClick={() => toggleList('verification_required', v)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-mono ${form.verification_required.includes(v) ? 'bg-[#0055FF] text-white border-[#0055FF]' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
                    data-testid={`ver-${v}`}
                  >{v.replace('_', ' ')}</button>
                ))}
              </div>
            </Field>
          </Section>

          {/* Section: Delivery */}
          <Section title="Delivery">
            <Field label="Delivery method">
              <div className="flex flex-wrap gap-2">
                {DELIVERY_METHODS.map(m => (
                  <button key={m.value} type="button" disabled={m.disabled} onClick={() => !m.disabled && toggleList('delivery_method', m.value)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-mono ${m.disabled ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : form.delivery_method.includes(m.value) ? 'bg-[#0055FF] text-white border-[#0055FF]' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
                    data-testid={`delivery-${m.value}`}
                  >{m.label}</button>
                ))}
              </div>
            </Field>
            <Field label="Contact automation" hint="P1 features — flag now, activated when live">
              <div className="flex flex-wrap gap-2">
                {CONTACT_AUTOMATION.map(m => (
                  <button key={m.value} type="button" disabled={m.disabled} onClick={() => !m.disabled && toggleList('contact_automation', m.value)}
                    className="px-3 py-1.5 rounded-full border text-xs font-mono bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                    data-testid={`auto-${m.value}`}
                  >{m.label}</button>
                ))}
              </div>
            </Field>
          </Section>

          <Section title="Notes">
            <Textarea rows={2} value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="Anything else sellers should know" data-testid="input-notes" />
          </Section>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="outline" onClick={() => navigate('/buyer')} data-testid="cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving || (!membershipLoading && !membership?.has_membership)} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="save-demand-btn">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Post demand profile'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.2em] font-mono text-gray-500 mb-3">{title}</div>
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-sm font-medium text-gray-800 mb-1 block">{label}</label>
    {hint && <div className="text-xs text-gray-500 mb-1.5">{hint}</div>}
    {children}
  </div>
);
